/*
Owner of the app config singleton — the `GET api/system/config` payload
(meta, selects, structures, categories, roleGroups, orgGroups, orgUtilities,
orgAdmin), cached in sessionStorage and served to every component through
the global mixin's `getConfig()`.

XOL-446. This used to be a bare `var config` at module scope in main.js:

  * nothing could reset it, so a logout left the previous org's config in
    memory and in sessionStorage;
  * main.js cannot be imported under Jest (it boots Vue and mounts #app —
    see the workaround at the top of routeGuard.spec.js), so none of it was
    testable.

It stays a module singleton rather than moving into Vuex, deliberately.
Reactivity was never the missing property: `updateConfig()` rebinds the
whole reference, so a component that snapshotted `getConfig().selects.x` in
`data()` keeps the old array either way — only converting those to
`computed` would fix that, and conventions.md explicitly blesses leaving the
Select components alone. Putting config in Vuex would also push the entire
org config into `localStorage['xolas-user']` via stripSensitiveState, which
is exactly what main.js deletes on every boot ("exposes too much info").

What was missing was resettability and single ownership. That is what this
module adds.

Babel 6: no optional chaining, no nullish coalescing, no `.finally()`.
------------------------------------------------------------------------ */

import { makeDefaultConfig } from '@/components/config/config'
import { CONFIG_CACHE_KEY, registerResetHook } from '@/lib/session'
import { setAppTimezone, resetAppTimezone } from '@/lib/appTime'

let config = makeDefaultConfig()

/**
 * Hand the payload's clock to `@/lib/appTime`.
 *
 * XOL-449. The API is the authority for the application timezone; `appTime`
 * has no default of its own and falls back to the browser's offset until it is
 * told otherwise. Called from BOTH writers below — `hydrateConfigFromSession()`
 * restores a cached payload without going through `setConfig()`, so hooking
 * only the fetch path would silently drop back to the browser's clock on every
 * page reload.
 */
function applyTimezone (payload, fromApi) {
  if (payload && payload.timezone) {
    setAppTimezone(payload.timezone)
    return
  }

  if (fromApi) {
    // An API older than XOL-448 sends no `timezone`, and the fallback is the
    // browser's own offset — correct for whoever is sitting in the app zone and
    // silently an hour or eight out for everyone else. Silence is what made the
    // original defect survive; this is the one place that can see it happening.
    //
    // `error` and not `warn`: the app is writing dates against an unverified
    // clock. That is a fault, not a caution, and it should show up in whatever
    // is filtering the console at error level.
    console.error(
      'appConfig: api/system/config carried no timezone. Falling back to the browser clock — ' +
      'dates will be wrong for any user outside the API\'s zone. Deploy the API side of XOL-448.'
    )
  }

  // The defaults carry no `timezone`, so a reset to defaults is a reset of the
  // clock rather than a stale offset left standing.
  resetAppTimezone()
}

function safeSessionStorage () {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }
  } catch (e) {
    // private mode / disabled storage
  }
  return null
}

export function getConfig () {
  return config
}

/**
 * Replace the config and mirror it to sessionStorage.
 */
export function setConfig (payload) {
  var fromApi = !!(payload && typeof payload === 'object')
  config = fromApi ? payload : makeDefaultConfig()
  applyTimezone(config, fromApi)
  var ss = safeSessionStorage()
  if (ss) {
    try {
      ss.setItem(CONFIG_CACHE_KEY, JSON.stringify(config))
    } catch (e) {
      // ignore quota / private-mode failures
    }
  }
  return config
}

/**
 * Back to a private copy of the defaults, and drop the cached copy.
 *
 * `makeDefaultConfig()` rather than the `defaultConfig` instance: main.js
 * has historically written into the config object it was handed
 * (`getConfig().meta[...] = ...`), so aliasing the module literal would
 * hand out an object polluted by the previous session.
 */
export function resetConfig () {
  config = makeDefaultConfig()
  applyTimezone(config)
  var ss = safeSessionStorage()
  if (ss) {
    try {
      ss.removeItem(CONFIG_CACHE_KEY)
    } catch (e) {
      // ignore
    }
  }
  return config
}

/**
 * Seed from the sessionStorage cache at boot, falling back to the defaults.
 */
export function hydrateConfigFromSession () {
  var ss = safeSessionStorage()
  if (ss) {
    try {
      var raw = ss.getItem(CONFIG_CACHE_KEY)
      if (raw) {
        var parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          config = parsed
          applyTimezone(config)
          return config
        }
      }
    } catch (e) {
      // corrupt cache — fall through to defaults
    }
  }
  config = makeDefaultConfig()
  applyTimezone(config)
  return config
}

// The config is per-organisation, so it must not outlive the session.
registerResetHook('appConfig', resetConfig)

export default {
  getConfig: getConfig,
  setConfig: setConfig,
  resetConfig: resetConfig,
  hydrateConfigFromSession: hydrateConfigFromSession
}
